import { boardSlice } from "./boardSlice";
import { FilteredPagedQueryResult, FilteredPagedModel, EntityCommonInfoModel, CommonCollectorModel } from "../../entities/common";
import {
  ComputedPeListModel,
  GlanceAdjustmentCollectModel,
  GlanceAdjustmentCommonInfoModel,
  GlanceAdjustmentMarkCollectModel,
  GlanceAdjustmentMarkModel,
  GlanceAdjustmentModel,
  GlanceHistoryCollectModel,
  GlanceModel,
  GlancesComputationModel,
  GlancesModel,
} from "../../entities/glance";
import { TagTypes } from "./boardSlice";
import { getQuerySearchParams } from "../../utils";

export const glanceApi = boardSlice.injectEndpoints({
  endpoints: (builder) => ({
    getGlanceCommonInfo: builder.query<EntityCommonInfoModel, undefined>({
      query: () => "/glance/commonInfo",
      providesTags: [{ type: TagTypes.Glance, id: "LIST" }],
    }),

    getGlancesFilteredPaged: builder.query<FilteredPagedQueryResult<GlanceModel>, FilteredPagedModel>({
      query: ({ page = 1, pageSize = 10, searchTerm = "", sortFieldName = "", descending = false, ...rest }: FilteredPagedModel) => {
        let url = `/glance/pageable?page=${page}&pageSize=${pageSize}&searchTerm=${searchTerm}&sortFieldName=${sortFieldName}&descending=${descending}`;
        const restParams = getQuerySearchParams(rest);
        if (restParams) {
          url += `&${restParams}`;
        }
        return url;
      },
      providesTags: (result, error, arg) =>
        result
          ? [
              { type: TagTypes.Glance, id: "PARTIAL-LIST" },
              ...result.data.map(({ id }) => ({
                type: TagTypes.Glance,
                id,
              })),
            ]
          : [{ type: TagTypes.Glance, id: "PARTIAL-LIST" }],
    }),
    getGlance: builder.query<GlanceModel, number>({
      query: (recordId) => `/glance/${recordId}`,
      providesTags: (result, error, arg) => [{ type: TagTypes.Glance, id: arg }],
    }),
    getGlancesCacheList: builder.query<GlancesModel, undefined>({
      query: () => "/glance/cache",
      providesTags: [{ type: TagTypes.Glance, id: "Cache-LIST" }],
    }),

    cacheDatabaseGlances: builder.mutation<GlanceModel[], undefined>({
      query: () => ({
        url: "/glance/cache/database",
        method: "POST",
      }),
      invalidatesTags: [{ type: TagTypes.Glance, id: "Cache-LIST" }],
    }),

    collectAtAGlance: builder.mutation<GlanceModel[], CommonCollectorModel>({
      query: (model) => ({
        url: "/glance/collect/atAGlance",
        method: "POST",
        body: model,
      }),
      invalidatesTags: [{ type: TagTypes.Glance, id: "Cache-LIST" }],
    }),

    collecWatcher: builder.mutation<GlancesModel, { init: boolean }>({
      query: (model: { init: boolean }) => ({
        url: "/glance/collect/watcher",
        method: "POST",
        body: model,
      }),
      invalidatesTags: [{ type: TagTypes.Glance, id: "Cache-LIST" }],
    }),

    initGlances: builder.mutation({
      query: () => ({
        url: "/glance/init",
        method: "POST",
      }),
      invalidatesTags: [{ type: TagTypes.Glance, id: "Cache-LIST" }],
    }),

    saveGlances: builder.mutation({
      query: () => ({
        url: "/glance/save",
        method: "POST",
      }),
      invalidatesTags: [{ type: TagTypes.Glance, id: "Cache-LIST" }],
    }),

    clearGlancesCache: builder.mutation({
      query: () => ({
        url: "/glance/clearCache",
        method: "DELETE",
      }),
      invalidatesTags: [{ type: TagTypes.Glance, id: "Cache-LIST" }],
    }),

    getGlancesComputation: builder.query<GlancesComputationModel, undefined>({
      query: () => "/glance/computations",
      providesTags: [{ type: TagTypes.Glance, id: "Computations-LIST" }],
    }),

    computeGlances: builder.mutation({
      query: () => ({
        url: "/glance/computations",
        method: "POST",
      }),
      invalidatesTags: [{ type: TagTypes.Glance, id: "Computations-LIST" }],
    }),

    getComputedPeList: builder.query<ComputedPeListModel, undefined>({
      query: () => "/glance/computedPe",
      providesTags: [{ type: TagTypes.ComputedPe, id: "ComputedPe-LIST" }],
    }),

    computePeList: builder.mutation({
      query: () => ({
        url: "/glance/computedPe",
        method: "POST",
      }),
      invalidatesTags: [{ type: TagTypes.ComputedPe, id: "ComputedPe-LIST" }],
    }),

    //

    collectGlancesHistory: builder.mutation({
      query: (model: GlanceHistoryCollectModel) => ({
        url: "/glance/collect/history",
        method: "POST",
        body: model,
      }),
      invalidatesTags: [
        { type: TagTypes.Glance, id: "LIST" },
        { type: TagTypes.Glance, id: "PARTIAL-LIST" },
      ],
    }),

    // GlanceAdjustment

    getGlanceAdjustmentCommonInfo: builder.query<GlanceAdjustmentCommonInfoModel, undefined>({
      query: () => "/glance/glanceAdjustment/commonInfo",
      providesTags: [{ type: TagTypes.GlanceAdjustment, id: "LIST" }],
    }),

    getGlanceAdjustmentsFilteredPaged: builder.query<FilteredPagedQueryResult<GlanceAdjustmentModel>, FilteredPagedModel>({
      query: ({ page = 1, pageSize = 10, searchTerm = "", sortFieldName = "", descending = false, ...rest }: FilteredPagedModel) => {
        let url = `/glance/glanceAdjustment/pageable?page=${page}&pageSize=${pageSize}&searchTerm=${searchTerm}&sortFieldName=${sortFieldName}&descending=${descending}`;
        const restParams = getQuerySearchParams(rest);
        if (restParams) {
          url += `&${restParams}`;
        }
        return url;
      },
      providesTags: (result, error, arg) =>
        result
          ? [
              { type: TagTypes.GlanceAdjustment, id: "PARTIAL-LIST" },
              ...result.data.map(({ id }) => ({
                type: TagTypes.GlanceAdjustment,
                id,
              })),
            ]
          : [{ type: TagTypes.GlanceAdjustment, id: "PARTIAL-LIST" }],
    }),
    collectGlanceAdjustments: builder.mutation({
      query: (model: GlanceAdjustmentCollectModel) => ({
        url: "/glance/collect/glanceAdjustment",
        method: "POST",
        body: model,
      }),
      invalidatesTags: [
        { type: TagTypes.GlanceAdjustment, id: "LIST" },
        { type: TagTypes.GlanceAdjustment, id: "PARTIAL-LIST" },
      ],
    }),

    // GlanceAdjustmentMark

    getGlanceAdjustmentMarkCommonInfo: builder.query<EntityCommonInfoModel, undefined>({
      query: () => "/glance/glanceAdjustmentMark/commonInfo",
      providesTags: [{ type: TagTypes.GlanceAdjustmentMark, id: "LIST" }],
    }),

    getGlanceAdjustmentMarksFilteredPaged: builder.query<FilteredPagedQueryResult<GlanceAdjustmentMarkModel>, FilteredPagedModel>({
      query: ({ page = 1, pageSize = 10, searchTerm = "", sortFieldName = "", descending = false, ...rest }: FilteredPagedModel) => {
        let url = `/glance/glanceAdjustmentMark/pageable?page=${page}&pageSize=${pageSize}&searchTerm=${searchTerm}&sortFieldName=${sortFieldName}&descending=${descending}`;
        const restParams = getQuerySearchParams(rest);
        if (restParams) {
          url += `&${restParams}`;
        }
        return url;
      },
      providesTags: (result, error, arg) =>
        result
          ? [
              { type: TagTypes.GlanceAdjustmentMark, id: "PARTIAL-LIST" },
              ...result.data.map(({ id }) => ({
                type: TagTypes.GlanceAdjustmentMark,
                id,
              })),
            ]
          : [{ type: TagTypes.GlanceAdjustmentMark, id: "PARTIAL-LIST" }],
    }),
    collectGlanceAdjustmentMarks: builder.mutation({
      query: (model: GlanceAdjustmentMarkCollectModel) => ({
        url: "/glance/collect/glanceAdjustmentMark",
        method: "POST",
        body: model,
      }),
      invalidatesTags: [
        { type: TagTypes.GlanceAdjustmentMark, id: "LIST" },
        { type: TagTypes.GlanceAdjustmentMark, id: "PARTIAL-LIST" },
      ],
    }),
  }),
});

export const {
  useGetGlanceCommonInfoQuery,

  useGetGlanceQuery,
  useGetGlancesFilteredPagedQuery,

  useCollectGlancesHistoryMutation,

  useGetGlancesCacheListQuery,
  useInitGlancesMutation,
  useSaveGlancesMutation,
  useCacheDatabaseGlancesMutation,
  useCollectAtAGlanceMutation,
  useCollecWatcherMutation,
  useClearGlancesCacheMutation,

  useGetGlancesComputationQuery,
  useComputeGlancesMutation,

  useGetComputedPeListQuery,
  useComputePeListMutation,

  useGetGlanceAdjustmentCommonInfoQuery,
  useGetGlanceAdjustmentsFilteredPagedQuery,
  useCollectGlanceAdjustmentsMutation,

  useGetGlanceAdjustmentMarkCommonInfoQuery,
  useGetGlanceAdjustmentMarksFilteredPagedQuery,
  useCollectGlanceAdjustmentMarksMutation,
} = glanceApi;
